/**
 * src/components/spatial/SpatialObjectListenCard.tsx
 *
 * Hearing a building, from the map.
 *
 * The player and the proximity panel were both already here, but the
 * player's open/closed state lived inside `SpatialBroadcastCard`, which
 * only ever renders for objects the visitor is standing next to. So the
 * hover card could offer Listen on a building across the city and have
 * nowhere to send the click. This is the missing half: a map-anchored
 * home for the same `SpatialBroadcastPlayer`, reachable from any object.
 *
 * Two things it deliberately does not do.
 *
 * It does not start a broadcast. Listening is a read; a visitor clicking
 * Listen must not be the reason a building begins to speak. If nothing
 * is live the card says so and offers another look, nothing more.
 *
 * And it does not ask for a wallet. `viewerWallet` and `country` are
 * passed to the player when the visitor happens to have connected one,
 * because the room's policy check uses them, but their absence is not a
 * reason to refuse to listen.
 *
 * The click that opens this card is also the user gesture the browser's
 * autoplay policy wants, which is why Listen is a click and not a hover.
 */

import React, { useEffect, useRef, useState } from 'react';

import { MapEntityTooltip } from '../map/MapEntityTooltip';
import { SpatialBroadcastPlayer } from './SpatialBroadcastPlayer';
import {
  getSpatialBroadcast,
  type SpatialBroadcastSession,
} from '../../services/spatial/spatialPresenceClient';
import { liveBroadcastInfo, type LiveBroadcastInfo } from '../../hooks/useLiveBroadcasts';

export interface SpatialObjectListenCardProps {
  objectId: string;
  objectName: string;
  /** Matches the stripe on the hover card this was opened from. */
  accentColor?: string;
  /** Where on the map the player is anchored, in container pixels. */
  anchor: { x: number; y: number };
  viewport?: { width: number; height: number } | null;
  /**
   * What the map-wide poll already knows about this object.
   *
   * When present the player connects immediately and no request is made:
   * the answer is seconds old at worst, and re-asking per click would be
   * a request to learn something already on screen.
   */
  known?: LiveBroadcastInfo | null;
  /** Metres to the object, when the host app knows where the visitor is. */
  distanceMeters?: number | null;
  wallet?: string | null;
  country?: number | null;
  onClose: () => void;
}

const CARD_WIDTH = 320;

const noteStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#94a3b8',
  lineHeight: 1.45,
};

const buttonStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 8,
  padding: '5px 12px',
  fontSize: 11,
  fontWeight: 700,
  background: 'rgba(99,102,241,0.3)',
  color: '#818cf8',
  cursor: 'pointer',
};

export const SpatialObjectListenCard: React.FC<SpatialObjectListenCardProps> = ({
  objectId,
  objectName,
  accentColor = '#6366f1',
  anchor,
  viewport = null,
  known = null,
  distanceMeters = null,
  wallet = null,
  country = null,
  onClose,
}) => {
  const [live, setLive] = useState<LiveBroadcastInfo | null>(known);
  // Only when we have to ask. Arriving with an answer should not flash a
  // spinner over a player that could already be connecting.
  const [checking, setChecking] = useState(!known);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  /**
   * Sessions we have watched end.
   *
   * The map-wide poll can still be listing a broadcast that finished
   * seconds ago, and without this the card would take that stale entry,
   * rejoin the dead room, be told it ended, and start again — flapping
   * until the next poll caught up. Having heard a session end is
   * knowledge the poll does not have yet.
   */
  const endedRef = useRef<Set<string>>(new Set());

  /**
   * Find out whether there is anything to join.
   *
   * Once a session is resolved this stops looking. A connected player is
   * in the room and hears it end; the map-wide poll is up to twenty
   * seconds stale, and letting it drop a session out from under a
   * playing card would cut off audio mid-sentence. `onEnded` is what
   * closes a broadcast here, not a poll that has not caught up.
   *
   * Cancellation is scoped to this effect rather than to the component,
   * so React's double-invoked development effects cannot leave a stale
   * "abandoned" flag set and silently discard the real answer.
   */
  useEffect(() => {
    if (live) return undefined;
    if (known && !endedRef.current.has(known.sessionId)) {
      setLive(known);
      setChecking(false);
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      setChecking(true);
      setError(null);
      const result = await getSpatialBroadcast(objectId);
      if (cancelled) return;
      setChecking(false);

      if (!result.ok) {
        setError('Could not find out what this object is doing.');
        return;
      }
      // A 200 with a null session is the honest "it is quiet" answer, so
      // an empty result is information rather than a failure. The same
      // rule the map-wide poll applies decides whether it is joinable.
      setLive(liveBroadcastInfo(
        (result as { session?: SpatialBroadcastSession | null }).session ?? null,
      ));
    })();

    return () => { cancelled = true; };
  }, [live, known, objectId, attempt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <MapEntityTooltip
      data-testid="spatial-object-listen-card"
      anchor={anchor}
      viewport={viewport}
      surfaceId={`object-listen-${objectId}`}
      surfaceKind="object_listen"
      width={CARD_WIDTH}
      estimatedHeight={live ? 240 : 140}
      accentColor={accentColor}
      title={objectName}
      badge={(
        <button
          type="button"
          aria-label="Close player"
          onClick={onClose}
          style={{
            border: 'none', background: 'rgba(255,255,255,0.1)', color: '#94a3b8',
            borderRadius: 4, padding: '1px 6px', fontSize: 12, cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    >
      {/* Tighter around the player, which brings its own panel chrome:
          full card padding on top of it reads as a box in a box. */}
      <div
        style={{
          padding: live ? 8 : '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {live ? (
          /* The existing player, unchanged. Its own close control is
             left off: this card's ✕ is the one to press, and two would
             be a question about which one ends what. */
          <SpatialBroadcastPlayer
            spatialObjectId={objectId}
            broadcasterId={live.broadcasterId}
            objectName={live.objectName || objectName}
            title={live.title}
            distanceMeters={distanceMeters}
            autoPlayAudio
            viewerWallet={wallet}
            country={country}
            onEnded={() => {
              endedRef.current.add(live.sessionId);
              setLive(null);
            }}
          />
        ) : checking ? (
          <div style={noteStyle}>Listening for {objectName}…</div>
        ) : (
          <>
            <div style={noteStyle}>
              {error || `${objectName} is not broadcasting right now.`}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={buttonStyle}
                onClick={() => setAttempt((n) => n + 1)}
              >
                Check again
              </button>
            </div>
          </>
        )}
      </div>
    </MapEntityTooltip>
  );
};

export default SpatialObjectListenCard;
